import {Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import {ListView} from "@/scenes.ui/list-view";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {EvolutionInfo} from "@/store/card/types";
import {WarriorClass} from "@/store/level/types";
import {CreatureUtils} from "@/utils/creature-utils";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";

export class EvolutionGrid extends VerticalStandardPanel {
    private static GIFTS_IN_ROW = 4;
    private listView!: ListView | null;
    private rowContainers: Container[] = [];

    constructor(scene: Scene, canvas: HTMLCanvasElement, evolutionInfo: EvolutionInfo, onForceClick: (selectedForce: WarriorClass) => void, selectedForce: WarriorClass, heroName: string) {
        super(scene, canvas, "");
        SpriteUtils.scale(this, 1.15 / CU.S);
        const selectedForceName = CreatureUtils.getClassName(selectedForce);
        const title = Tr("evolution.gifts").replace("[selectedClass]", selectedForceName).replace("[hero name]", heroName);
        let topText = TextUtils.addTextByFamily(scene, title, 0, -220, "#e7e7e7", 14, "normal", 420, Fonts.ARIAL_MT);
        topText.setOrigin(0.5, 0);
        this.add(topText);
        let secondText = TextUtils.addTextByFamily(scene, Tr("evolution.selectForce"), 0, -180, "#ffd465", 14, "normal", 420, Fonts.ARIAL_MT);
        secondText.setOrigin(0.5, 0);
        this.add(secondText);
        let listPanelContainer: Container[] = [];
        const filteredClasses = (evolutionInfo.availableClasses ?? []).filter((wClass) => {
            const atlas = CreatureUtils.getForceAtlasLarge(wClass);
            const iconKey = CreatureUtils.getLargeForceIcon(wClass, true);
            const className = CreatureUtils.getClassName(wClass);
            return !!atlas && !!iconKey && !!className && className.trim() !== "" && className !== "-";
        });
        if (filteredClasses.length === 0) return;
        filteredClasses.forEach((wClass, i) => {
            const rowIndex = Math.floor(i / EvolutionGrid.GIFTS_IN_ROW);
            if (!this.rowContainers[rowIndex]) {
                this.rowContainers[rowIndex] = new Container(scene, 0, 0);
                listPanelContainer.push(this.rowContainers[rowIndex]);
                this.rowContainers[rowIndex].height = 130;
            }
            const colIndex = i % EvolutionGrid.GIFTS_IN_ROW;
            const xPos = -200 * CU.S + colIndex * 120 * CU.S;
            const yPos = 50 * CU.S;
            let giftItem = new EvolutionGiftItemPanel(scene, wClass, xPos, yPos, () => {
                onForceClick(wClass);
                this.destroy();
            });
            this.rowContainers[rowIndex].add(giftItem);
        });
        const textBounds = secondText.getBounds();
        const listViewY = textBounds.bottom + 10 * CU.S;
        let listView = new ListView(scene, canvas, listPanelContainer, -canvas.width / 2 + 362 * CU.S, listViewY, 500 * CU.S, 490 * CU.S);
        listView.height = this.rowContainers.length * 200 / CU.S;
        this.listView = listView;
        this.scene.add.existing(listView);
        listView.setDepth(3002);
        this.on("destroy", () => {
            if (this.listView) {
                this.listView.destroy();
                this.listView = null;
            }
        });
    }

    protected onClose(): void {
        if (this.listView) {
            this.listView.destroy();
            this.listView = null;
        }
        this.destroy();
    }
}

class EvolutionGiftItemPanel extends Container {
    constructor(scene: Scene, wClass: WarriorClass, x: number, y: number, onClick: () => void) {
        super(scene, x, y);
        const atlas = CreatureUtils.getForceAtlasLarge(wClass);
        const iconKey = CreatureUtils.getLargeForceIcon(wClass, true);
        const className = CreatureUtils.getClassName(wClass);
        let icon = SpriteUtils.addSprite(scene, 0, 0, atlas, iconKey, 1);
        icon.setOrigin(0.5);
        let label = TextUtils.addTextByFamily(scene, className, 0, 40, "#ffffff", 14, "bold", 120, Fonts.ARIAL_MT);
        label.setOrigin(0.5, 0);
        this.add([icon, label]);
        SpriteUtils.scale(this, 0.75 / CU.S);
        this.setSize(120, 120);
        this.setInteractive(new Phaser.Geom.Circle(60, 60, 60), Phaser.Geom.Circle.Contains);
        this.on("pointerup", onClick);
    }
}
